import { useSearchParams } from "react-router-dom";
// components/meet/GroupMeeting.js
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FaMicrophone,
  FaMicrophoneSlash,
  FaVideo,
  FaVideoSlash,
  FaDesktop,
  FaStopCircle,
} from "react-icons/fa";
import { IoCallOutline, IoExit } from "react-icons/io5";
import { createCallSession, idleCall } from "./callSession";
import DraggableStream from "./DraggableStream";
import FloatingCallOverlay from "./FloatingCallOverlay";
import { RemoteAudio, StreamVideo } from "./StreamMedia";
import "./meeting.css";

export default function GroupMeeting({
  roomId,
  userId,
  userName,
  setOnCall,
  selectedGroupName,
  meetingFullScreen,
  setMeetingFullScreen,
  currentUser,
  users,
  spaceId,
  stationId,
  callType,
  currentrecipient,
  isChatOpen = true,
  chatTarget,
  onReturnToCall,
}) {
  const dispatch = useDispatch();
  const [callParams, setCallParams] = useSearchParams();
  const acceptedCall = useRef(null);
  const participants = useSelector((state) => state.videoCall.participants);
  const session = useRef(null);
  const mounted = useRef(true);
  const stageRef = useRef(null);
  const audioElements = useRef(new Map());
  const [call, setCall] = useState(idleCall);
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [notice, setNotice] = useState("");
  const joined = call.phase === "joined";
  const busy = call.phase === "joining" || call.phase === "leaving";
  const sameRoom = !call.target || call.target.roomId === roomId;
  const visible = meetingFullScreen || (isChatOpen && sameRoom);
  const onAudioBlocked = useCallback(() => setAudioBlocked(true), []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      session.current?.leave();
    };
  }, []);
  useEffect(() => {
    setOnCall(joined && sameRoom);
  }, [joined, sameRoom, setOnCall]);
  useEffect(() => {
    if (visible) setDismissed(false);
  }, [visible]);
  useEffect(() => {
    if (!notice) return;
    const timeout = setTimeout(() => setNotice(""), 3000);
    return () => clearTimeout(timeout);
  }, [notice]);
  useEffect(() => {
    if (call.screenStream) setSelectedScreen(userId);
  }, [call.screenStream, userId]);

  const join = (startWithCamera = true) => {
    if (
      busy ||
      joined ||
      !roomId ||
      !userId ||
      (session.current && session.current.state.phase !== "idle")
    )
      return;
    if (!navigator.mediaDevices?.getUserMedia) {
      setCall({
        ...idleCall,
        error:
          "Calls require HTTPS (or localhost) and a browser with camera/microphone support.",
      });
      return;
    }
    setNotice("");
    setDismissed(false);
    setAudioBlocked(false);
    session.current = createCallSession({
      dispatch,
      target: {
        roomId,
        expectedCallId: callParams.get("acceptCall") || null,
        userId,
        userName,
        selectedGroupName,
        currentUser,
        users,
        spaceId,
        stationId,
        callType,
        currentrecipient,
        chatTarget,
      },
      onChange: (next) => {
        if (!mounted.current) return;
        setCall(next);
        // A remote hang-up reaches the session directly, bypassing the Leave button.
        if (next.phase === "leaving") {
          setMeetingFullScreen(false);
          setOnCall(false);
          setSelectedScreen(null);
          setNotice(
            next.endedRemotely ? "The call ended" : "You left the call",
          );
        }
      },
    });
    session.current.join(startWithCamera);
  };
  useEffect(() => {
    const id = callParams.get("acceptCall");
    const targetRoom = callParams.get("acceptRoom");
    if (
      !id ||
      !userId ||
      targetRoom !== roomId ||
      acceptedCall.current === id ||
      busy ||
      joined
    )
      return;
    acceptedCall.current = id;
    join();
    const next = new URLSearchParams(callParams);
    next.delete("acceptCall");
    next.delete("acceptRoom");
    setCallParams(next, { replace: true });
  }, [callParams, roomId, busy, joined, userId]);
  const leave = () => {
    if (call.phase !== "joined" && call.phase !== "joining") return;
    session.current.leave();
    setMeetingFullScreen(false);
    setOnCall(false);
    setSelectedScreen(null);
    if (joined) setNotice("You left the call");
  };
  const returnToCall = () => {
    onReturnToCall?.(call.target?.chatTarget);
    setMeetingFullScreen(true);
  };
  const resumeAudio = () => {
    // Execute play() directly inside this click, not after an asynchronous step.
    const attempts = [...audioElements.current.values()].map((audio) =>
      audio.play(),
    );
    Promise.allSettled(attempts).then((results) => {
      if (mounted.current)
        setAudioBlocked(results.some((result) => result.status === "rejected"));
    });
  };

  const remote = joined
    ? participants.filter((person) => person.userId !== userId)
    : [];
  const shares = remote
    .filter((person) => person.screenSharing && person.screenStream)
    .map((person) => ({
      ownerId: person.userId,
      name: person.name,
      stream: person.screenStream,
    }));
  if (call.screenStream)
    shares.unshift({
      ownerId: userId,
      name: userName,
      stream: call.screenStream,
      local: true,
    });
  const activeShare =
    shares.find((share) => share.ownerId === selectedScreen) || shares[0];
  const floatingPerson = remote.find((person) => person.stream);
  const floatingData =
    activeShare && !activeShare.local
      ? {
          stream: activeShare.stream,
          isCameraOn: true,
          isScreenShare: true,
          userName: `${activeShare.name || "Participant"}'s screen`,
        }
      : activeShare?.local
        ? {
            stream: call.localStream,
            isCameraOn: call.isCameraOn,
            isMuted: call.isMuted,
            userName: "You are sharing",
          }
        : floatingPerson
          ? {
              stream: floatingPerson.stream,
              isCameraOn: !!floatingPerson.camera,
              isMuted: !floatingPerson.mic,
              userName: floatingPerson.name,
            }
          : {
              stream: call.localStream,
              isCameraOn: call.isCameraOn,
              isMuted: call.isMuted,
              userName,
            };
  const canShare =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getDisplayMedia;

  const cameraTile = (person) => (
    <div className="ant-call-camera-tile" key={person.userId}>
      {person.camera && person.stream ? (
        <StreamVideo stream={person.stream} />
      ) : (
        <div className="ant-call-camera-off">
          <FaVideoSlash />
          <span>{person.name || "Participant"}'s camera is off</span>
        </div>
      )}
      {!person.mic && (
        <span className="ant-call-mic-badge">
          <FaMicrophoneSlash />
        </span>
      )}
      <span className="ant-call-name">{person.name || "Participant"}</span>
    </div>
  );

  return (
    <>
      <div
        className={`ant-call ${meetingFullScreen ? "ant-call-fullscreen" : ""}`}
      >
        {joined ? (
          visible ? (
            <>
              <div className="ant-call-stage" ref={stageRef}>
                {activeShare ? (
                  <div className="ant-call-screen-stage">
                    {activeShare.local ? (
                      <div className="ant-call-sharing-status">
                        <FaDesktop size={32} />
                        <strong>Your screen is being shared</strong>
                        <span>Open the content you want to show.</span>
                      </div>
                    ) : (
                      <StreamVideo stream={activeShare.stream} contain />
                    )}
                    <div
                      className="ant-call-share-picker"
                      aria-label="Shared screens"
                    >
                      {shares.map((share) => (
                        <button
                          type="button"
                          key={share.ownerId}
                          aria-pressed={activeShare.ownerId === share.ownerId}
                          onClick={() => setSelectedScreen(share.ownerId)}
                        >
                          {share.local
                            ? "Your screen"
                            : `${share.name || "Participant"}'s screen`}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : remote.length ? (
                  <div className="ant-call-camera-grid">
                    {remote.map(cameraTile)}
                  </div>
                ) : (
                  <div className="ant-call-sharing-status">
                    {call.target?.callType === "private"
                      ? "Waiting for an answer…"
                      : "Waiting for others to join…"}
                  </div>
                )}
                {call.localStream && (
                  <DraggableStream
                    boundsRef={stageRef}
                    stream={call.localStream}
                    userName={userName}
                    isMuted={call.isMuted}
                    isCameraOn={call.isCameraOn}
                  />
                )}
              </div>
              {activeShare && remote.length > 0 && (
                <div className="ant-call-camera-strip">
                  {remote.map(cameraTile)}
                </div>
              )}
              <div className="ant-call-controls">
                <button
                  type="button"
                  onClick={() => session.current.toggleMute()}
                  title={call.isMuted ? "Unmute" : "Mute"}
                  aria-label={
                    call.isMuted ? "Unmute microphone" : "Mute microphone"
                  }
                >
                  {call.isMuted ? <FaMicrophone /> : <FaMicrophoneSlash />}
                  {meetingFullScreen && (
                    <span>{call.isMuted ? "Unmute" : "Mute"}</span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => session.current.toggleCamera()}
                  title={call.isCameraOn ? "Camera off" : "Camera on"}
                  aria-label={
                    call.isCameraOn ? "Turn camera off" : "Turn camera on"
                  }
                >
                  {call.isCameraOn ? <FaVideoSlash /> : <FaVideo />}
                  {meetingFullScreen && (
                    <span>{call.isCameraOn ? "Camera off" : "Camera on"}</span>
                  )}
                </button>
                <button
                  type="button"
                  className={call.screenStream ? "ant-call-share-active" : ""}
                  disabled={call.shareBusy || !canShare}
                  title={
                    !canShare
                      ? "Screen sharing is unavailable in this browser; receiving shared screens still works."
                      : "Share screen"
                  }
                  aria-label={
                    call.screenStream ? "Stop sharing screen" : "Share screen"
                  }
                  onClick={() =>
                    call.screenStream
                      ? session.current.stopScreenShare()
                      : session.current.startScreenShare()
                  }
                >
                  {call.shareBusy ? (
                    <span className="ant-call-spinner" />
                  ) : call.screenStream ? (
                    <FaStopCircle />
                  ) : (
                    <FaDesktop />
                  )}
                  {meetingFullScreen && (
                    <span>
                      {call.screenStream ? "Stop sharing" : "Share screen"}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className="ant-call-leave"
                  onClick={leave}
                  aria-label="Leave call"
                >
                  <IoExit />
                  {meetingFullScreen && <span>Leave</span>}
                </button>
              </div>
            </>
          ) : (
            <button
              type="button"
              className="ant-call-join"
              onClick={returnToCall}
            >
              Return to your active call
            </button>
          )
        ) : (
          <div className="ant-call-join-row" aria-busy={busy}>
            <button
              type="button"
              className="ant-call-join"
              onClick={() => join(false)}
              disabled={busy || !roomId || !userId}
              title={
                !roomId ? "Select a chat first" : "Start with your camera off"
              }
            >
              <IoCallOutline aria-hidden="true" />
              <span>
                {callType === "private" ? "Voice call" : "Join with mic"}
              </span>
            </button>
            <button
              type="button"
              className="ant-call-join"
              onClick={() => join(true)}
              disabled={busy || !roomId || !userId}
              title={
                !roomId ? "Select a chat first" : "Start with your camera on"
              }
            >
              <FaVideo aria-hidden="true" />
              <span>
                {callType === "private" ? "Video call" : "Join with camera"}
              </span>
            </button>
            {busy && (
              <div className="ant-call-joining-overlay" role="status">
                <span className="ant-call-spinner" aria-hidden="true" />
                <span>
                  {call.phase === "joining" ? "Joining…" : "Ending call…"}
                </span>
                {call.phase === "joining" && (
                  <button type="button" onClick={leave}>
                    Cancel
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {joined && call.ringtoneBlocked && (
          <button
            type="button"
            onClick={() => session.current?.resumeRingtone()}
          >
            Enable ringtone
          </button>
        )}
        {call.error && (
          <p className="ant-call-error" role="status">
            {call.error}
          </p>
        )}
      </div>
      {remote.map((person) => (
        <RemoteAudio
          key={person.userId}
          userId={person.userId}
          stream={person.stream}
          elements={audioElements}
          onBlocked={onAudioBlocked}
        />
      ))}
      <FloatingCallOverlay
        visible={joined && !visible && !dismissed}
        data={floatingData}
        onOpen={returnToCall}
        onClose={() => setDismissed(true)}
      />
      {typeof document !== "undefined" &&
        (notice || (joined && audioBlocked)) &&
        createPortal(
          <div className="ant-call-notice" role="status">
            {joined && audioBlocked ? (
              <button type="button" onClick={resumeAudio}>
                Enable call audio
              </button>
            ) : (
              notice
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
